import React from 'react';
import { PdfLayoutGroup, PdfLayoutSection } from '../../../utils/api/pdfLayout';

interface Props {
  group: PdfLayoutGroup;
  section: PdfLayoutSection;
}

// "Descriptive Text" questions are display-only — they take no answer.
const DESCRIPTIVE_TEXT_TYPE_ID = 16;

const SectionBlock: React.FC<Props> = ({ group, section }) => {
  const isTable = group.layout_type === 'TABLE';
  const isGrid = group.layout_type === 'GRID';
  const hasBorder = isTable || section.question_appearance === 'border';
  const hasComments = isTable && group.questions.some((q) => q.can_comment);

  // width is applied by DocumentPreview on the item wrapper, not here
  const styleConfig = section.style_config ?? {};

  const description = group.description && (
    <div
      className='ple-section-block__desc'
      style={styleConfig.description_style}
    >
      {group.description}
    </div>
  );

  const name = (
    <div className='ple-section-block__name' style={styleConfig.name_style}>
      {group.name}
      {description}
    </div>
  );

  // GRID: render columns from table_headers, one row per question.
  if (isGrid) {
    const headers = group.table_headers ?? [];
    const gridStyle = styleConfig.grid_style ?? {};
    const columnWidths = gridStyle.widthPerColumn ?? {};
    const hasWidths = Object.keys(columnWidths).length > 0;

    return (
      <div className='ple-block ple-section-block is-bordered'>
        {name}
        <table className={`ple-grid ${hasWidths ? 'has-widths' : ''}`}>
          <thead>
            <tr>
              {headers.map((header, i) => (
                <th
                  key={i}
                  style={{
                    ...(gridStyle.tableHeaderStyle as React.CSSProperties),
                    width: columnWidths[i] + '%',
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {group.questions.length ? (
              group.questions.map((q) => (
                <tr key={q.id}>
                  {q.label !== '' && (
                    <td key={1} style={section.style_config?.question_style}>
                      <span className='ple-grid__cell'>{q.label}</span>
                    </td>
                  )}
                  {headers.slice(q.label !== '' ? 1 : 0).map((_, colIdx) => (
                    <td key={colIdx} style={section.style_config?.question_style}>
                      <span className='ple-grid__cell' />
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={Math.max(headers.length, 1)} style={section.style_config?.question_style}>No rows</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  if (isTable) {
    return (
      <div
        className={`ple-block ple-section-block is-table ${
          hasComments ? 'is-commented' : ''
        }`}
      >
        <div className='ple-table'>
          <div className='ple-table__head'>
            {name}
            {hasComments && (
              <div className='ple-table__comments-head'>Comments</div>
            )}
          </div>

          {group.questions.length ? (
            group.questions.map((q) => {
              const descriptive =
                q.question_type_id === DESCRIPTIVE_TEXT_TYPE_ID;

              if (descriptive) {
                return (
                  <div key={q.id} className='ple-table__row is-descriptive'>
                    <div className='ple-table__label'>{q.label}</div>
                  </div>
                );
              }

              return (
                <div key={q.id} className='ple-table__row'>
                  <div className='ple-table__label' style={section.style_config?.question_style}>{q.label}</div>
                  <div
                    className={`ple-table__answer ${q.can_comment ? 'ple-table__answer-with-border' : ''}`}
                    style={section.style_config?.question_style}
                  />
                  <div className='ple-table__comment' style={section.style_config?.question_style} />
                </div>
              );
            })
          ) : (
            <div className='ple-table__row is-descriptive'>
              <div className='ple-table__label ple-table__label--empty'>
                No questions
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`ple-block ple-section-block ${hasBorder ? 'is-bordered' : ''}`}
    >
      {name}
      <div className='ple-section-block__questions'>
        {group.questions.length ? (
          group.questions.map((q) => (
            <div
              key={q.id}
              className='ple-question'
              style={{
                flexBasis: `${q.width || 100}%`,
                ...section.style_config?.question_style,
              }}
            >
              <span className='ple-question__label'>{q.label}</span>
              {(section.question_appearance === 'underline' || hasBorder) &&
                q.question_type_id !== DESCRIPTIVE_TEXT_TYPE_ID && (
                  <span className='ple-question__answer' />
                )}
            </div>
          ))
        ) : (
          <div className='ple-question ple-question--empty'>No questions</div>
        )}
      </div>
    </div>
  );
};

export default SectionBlock;
